/**
 * Schedules management page — Phase 1.8 Pass 5.
 *
 * Lists keepa_query_schedules rows with their template binding, cron
 * expression, next fire countdown, monthly cap usage, and reconfirm status.
 * Per-row actions: pause/resume (PATCH enabled), reconfirm (POST), delete.
 * "New schedule" opens a modal with template picker + parameter form +
 * cron input with live "next fire at" preview.
 *
 * Reconfirm banner at the top surfaces any schedule with
 * reconfirmRequiredAt < now + 7d, agent-rule guardrail.
 */
import { useEffect, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { AlertTriangle, ChevronLeft, Pause, Play, Trash2, Zap } from 'lucide-react';
import type {
  KeepaQuerySchedule,
  KeepaQueryTemplate,
  SortRotationEntry,
  TemplateParameterSpec,
} from '@sourcito/schemas';
import {
  Badge,
  Button,
  Card,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from '../design';
import {
  useCreateKeepaSchedule,
  useDeleteKeepaSchedule,
  useKeepaSchedules,
  usePreviewCron,
  useReconfirmKeepaSchedule,
  useRunKeepaScheduleNow,
  useUpdateKeepaSchedule,
} from '../lib/keepaSchedules';
import { useKeepaTemplates } from '../lib/keepaTemplates';
import {
  TemplateParameterForm,
  type TemplateParameterValues,
} from '../components/TemplateParameterForm';

const CRON_PRESETS: Array<{ label: string; expression: string }> = [
  { label: 'Every Monday 9am UTC', expression: '0 9 * * 1' },
  { label: 'Every weekday 9am UTC', expression: '0 9 * * 1-5' },
  { label: 'Every day 7am UTC', expression: '0 7 * * *' },
  { label: 'Every hour', expression: '0 * * * *' },
  { label: 'Every 15 minutes', expression: '*/15 * * * *' },
  { label: 'Custom…', expression: '' },
];

const RECONFIRM_BANNER_THRESHOLD_DAYS = 7;

/**
 * Recommended sort rotation — 5 distinct "lenses" on the catalog. Surfaces
 * high-velocity proven products, established brands with social proof, volume
 * movers, niche/long-tail, and newly-listed entrants. Operator can drop /
 * reorder / add via the API; this is the one-click "good defaults" preset.
 */
const DEFAULT_SORT_ROTATION: SortRotationEntry[] = [
  { sortBy: 'bsr', sortOrder: 'asc' },
  { sortBy: 'reviewCount', sortOrder: 'desc' },
  { sortBy: 'monthlySold', sortOrder: 'desc' },
  { sortBy: 'bsr', sortOrder: 'desc' },
  { sortBy: 'reviewCount', sortOrder: 'asc' },
];

const EMPTY_RUNS_PAUSE_THRESHOLD = 3;

export function SchedulesPage() {
  const [createOpen, setCreateOpen] = useState(false);
  const schedules = useKeepaSchedules();
  const templates = useKeepaTemplates();

  const rows = schedules.data ?? [];
  const needsReconfirm = rows.filter(
    (r) => r.enabled && daysUntil(r.reconfirmRequiredAt) < RECONFIRM_BANNER_THRESHOLD_DAYS
  );

  return (
    <div className="min-h-screen bg-canvas text-fg-primary">
      <header className="border-b border-border-subtle">
        <div className="max-w-7xl mx-auto px-8 py-4 flex items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <Link
              to="/search"
              className="text-fg-tertiary hover:text-fg-primary transition-colors duration-fast"
            >
              <ChevronLeft className="size-5" />
            </Link>
            <div>
              <h1 className="text-xl font-medium leading-tight">Schedules</h1>
              <p className="text-xs text-fg-tertiary">
                Recurring template runs with monthly run caps + 30-day re-confirmation.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={() => setCreateOpen(true)} size="sm">
              New schedule
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-8 py-10 space-y-4">
        {needsReconfirm.length > 0 && (
          <Card className="border-warning/30 bg-warning/[0.06]">
            <div className="flex items-start gap-3">
              <AlertTriangle className="size-5 text-warning shrink-0 mt-0.5" />
              <div className="flex-1 space-y-1.5">
                <p className="text-sm font-medium text-fg-primary">
                  {needsReconfirm.length} schedule
                  {needsReconfirm.length === 1 ? '' : 's'} need reconfirmation
                </p>
                <p className="text-sm text-fg-secondary">
                  Sourcito pauses unattended schedules every 30 days. Re-confirm each to keep it
                  running.
                </p>
                <ul className="text-sm text-fg-secondary space-y-0.5 mt-1">
                  {needsReconfirm.map((s) => (
                    <li key={s.id}>
                      <strong className="text-fg-primary">{s.name}</strong> —{' '}
                      {relativeTime(new Date(s.reconfirmRequiredAt), {
                        past: 'expired ',
                        future: '',
                      })}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </Card>
        )}

        {schedules.isLoading && <p className="text-sm text-fg-secondary">Loading…</p>}
        {schedules.isError && (
          <Card className="border-negative/30 bg-negative/[0.06]">
            <p className="text-sm text-negative">
              Failed to load schedules: {(schedules.error as Error).message}
            </p>
          </Card>
        )}

        {schedules.data && rows.length === 0 && (
          <Card className="text-center py-10">
            <p className="text-sm text-fg-secondary">
              No schedules yet. Click <strong>New schedule</strong> to set up a recurring template
              run.
            </p>
          </Card>
        )}

        {rows.length > 0 && (
          <div className="border border-border-subtle rounded-md bg-surface overflow-hidden">
            <table className="w-full text-sm tabular-nums">
              <thead className="border-b border-border-subtle bg-surface-elev">
                <tr className="text-xs uppercase tracking-wider text-fg-tertiary">
                  <th className="text-left px-4 py-2.5 font-medium">Name</th>
                  <th className="text-left px-4 py-2.5 font-medium">Template</th>
                  <th className="text-left px-4 py-2.5 font-medium">Cron</th>
                  <th className="text-left px-4 py-2.5 font-medium">Next fire</th>
                  <th className="text-left px-4 py-2.5 font-medium">Enabled</th>
                  <th className="text-left px-4 py-2.5 font-medium">Usage</th>
                  <th className="text-left px-4 py-2.5 font-medium">Reconfirm</th>
                  <th className="text-left px-4 py-2.5 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <ScheduleRow
                    key={r.id}
                    schedule={r}
                    templateName={templates.data?.find((t) => t.id === r.templateId)?.name ?? '—'}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>New schedule</DialogTitle>
          </DialogHeader>
          <CreateScheduleForm
            templates={templates.data ?? []}
            onClose={() => setCreateOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ScheduleRow({
  schedule,
  templateName,
}: {
  schedule: KeepaQuerySchedule;
  templateName: string;
}) {
  const update = useUpdateKeepaSchedule(schedule.id);
  const reconfirm = useReconfirmKeepaSchedule();
  const del = useDeleteKeepaSchedule();
  const runNow = useRunKeepaScheduleNow();
  const [runFeedback, setRunFeedback] = useState<string | null>(null);
  // Auto-clear the inline "queued — runs in ≤60s" confirmation so it doesn't
  // linger and become misleading once a fresh cron tick has likely landed.
  useEffect(() => {
    if (!runFeedback) return;
    const t = setTimeout(() => setRunFeedback(null), 8000);
    return () => clearTimeout(t);
  }, [runFeedback]);
  const next = new Date(schedule.nextFireAt);
  const reconfirmAt = new Date(schedule.reconfirmRequiredAt);
  const reconfirmDays = daysUntil(schedule.reconfirmRequiredAt);
  const reconfirmColor =
    reconfirmDays < 0 ? 'text-negative' : reconfirmDays < 7 ? 'text-warning' : 'text-fg-tertiary';

  return (
    <tr className="border-t border-border-subtle hover:bg-surface-elev align-top">
      <td className="px-4 py-2.5 font-medium">{schedule.name}</td>
      <td className="px-4 py-2.5 text-xs text-fg-secondary">{templateName}</td>
      <td className="px-4 py-2.5 font-mono text-xs">{schedule.cronExpression}</td>
      <td className="px-4 py-2.5 text-xs">
        <time dateTime={schedule.nextFireAt} title={next.toLocaleString()}>
          {relativeTime(next)}
        </time>
      </td>
      <td className="px-4 py-2.5">
        {!schedule.enabled && schedule.consecutiveEmptyRuns >= EMPTY_RUNS_PAUSE_THRESHOLD ? (
          <Badge
            variant="warning"
            className="text-[10px]"
            title="Auto-paused — last 3 fires found no new products. Broaden the template's filters to discover more."
          >
            catalog exhausted
          </Badge>
        ) : (
          <Badge variant={schedule.enabled ? 'positive' : 'neutral'} className="text-[10px]">
            {schedule.enabled ? 'enabled' : 'paused'}
          </Badge>
        )}
      </td>
      <td className="px-4 py-2.5 text-xs">
        <div>
          {schedule.runsThisWindow} / {schedule.monthlyRunCap}
        </div>
        {(schedule.pageCursor > 0 || schedule.sortIndex > 0 || schedule.sortRotation) && (
          <div className="text-fg-tertiary text-[10px] mt-0.5">
            {schedule.sortRotation && schedule.sortRotation.length > 0
              ? `sort ${schedule.sortIndex + 1}/${schedule.sortRotation.length} · page ${schedule.pageCursor}`
              : `page ${schedule.pageCursor}`}
          </div>
        )}
      </td>
      <td className={`px-4 py-2.5 text-xs ${reconfirmColor}`}>
        <time dateTime={schedule.reconfirmRequiredAt} title={reconfirmAt.toLocaleString()}>
          {reconfirmDays < 0 ? `expired ${Math.abs(reconfirmDays)}d ago` : `in ${reconfirmDays}d`}
        </time>
      </td>
      <td className="px-4 py-2.5">
        <div className="flex items-center gap-1.5 flex-wrap">
          {/*
            "Run now" queues a one-off keepa_search_runs row for this schedule.
            It bypasses the monthly cap, the reconfirm gate, and the
            paused-state check by design (the click IS the consent). The
            actual execution happens on the next sub-sweep B tick (≤60s) —
            no synchronous wait here. Inline feedback confirms the queue
            landed; an error alert surfaces template/fee-profile gate trips.
          */}
          <Button
            variant="secondary"
            size="sm"
            aria-label="Queue an immediate one-off run"
            title="Queue a one-off run now — doesn't count toward your monthly cap"
            leadingIcon={<Zap className="size-3.5" />}
            loading={runNow.isPending}
            onClick={() =>
              runNow.mutate(schedule.id, {
                onSuccess: () => setRunFeedback('Queued — runs in ≤60s'),
                onError: (err) =>
                  window.alert(`Couldn't queue this schedule: ${(err as Error).message}`),
              })
            }
          >
            Run now
          </Button>
          {runFeedback && (
            <span className="text-xs text-positive" role="status">
              {runFeedback}
            </span>
          )}
          {reconfirmDays < RECONFIRM_BANNER_THRESHOLD_DAYS && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => reconfirm.mutate(schedule.id)}
              loading={reconfirm.isPending}
            >
              Reconfirm
            </Button>
          )}
          {/*
            Pause/Resume + Stop-and-delete are two distinct destructive actions
            on a recurring crawl. Pause flips `enabled` (the row stays, history
            preserved, resume later). Delete hard-removes the schedule row.
            Show explicit text alongside the icon — icon-only was a usability
            issue when both actions sit next to each other.
          */}
          <Button
            variant="ghost"
            size="sm"
            aria-label={schedule.enabled ? 'Pause crawl' : 'Resume crawl'}
            leadingIcon={
              schedule.enabled ? <Pause className="size-3.5" /> : <Play className="size-3.5" />
            }
            onClick={() => update.mutate({ enabled: !schedule.enabled })}
            loading={update.isPending}
          >
            {schedule.enabled ? 'Pause' : 'Resume'}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            aria-label="Stop and delete crawl"
            leadingIcon={<Trash2 className="size-3.5" />}
            onClick={() => {
              if (
                window.confirm(
                  `Stop this crawl and delete its schedule?\n\n"${schedule.name}" will no longer fire. History stays. To resume later, you'd have to re-create the schedule. Use Pause instead if you just want a temporary stop.`
                )
              ) {
                del.mutate(schedule.id);
              }
            }}
            loading={del.isPending}
          >
            Delete
          </Button>
        </div>
      </td>
    </tr>
  );
}

function CreateScheduleForm({
  templates,
  onClose,
}: {
  templates: KeepaQueryTemplate[];
  onClose: () => void;
}) {
  const [templateId, setTemplateId] = useState<string>('');
  const [name, setName] = useState('');
  const [parameterValues, setParameterValues] = useState<TemplateParameterValues>({});
  const [presetIdx, setPresetIdx] = useState(0);
  const [customCron, setCustomCron] = useState('*/15 * * * *');
  const [monthlyRunCap, setMonthlyRunCap] = useState(30);
  const [useRotation, setUseRotation] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [previewResult, setPreviewResult] = useState<
    { valid: true; nextFireAt: string } | { valid: false; error: string } | null
  >(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const create = useCreateKeepaSchedule();
  const previewCron = usePreviewCron();

  const tpl = templates.find((t) => t.id === templateId) ?? null;

  const cronExpression = CRON_PRESETS[presetIdx]!.expression || customCron;

  function handlePreview() {
    if (!cronExpression) return;
    previewCron.mutate(cronExpression, {
      onSuccess: (result) => setPreviewResult(result),
    });
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError(null);
    if (!templateId || !name.trim()) {
      setSubmitError('Pick a template and name your schedule.');
      return;
    }
    if (!acknowledged) {
      setSubmitError(
        'Please acknowledge that this schedule will run unattended (re-confirmation every 30 days).'
      );
      return;
    }

    try {
      await create.mutateAsync({
        templateId,
        name: name.trim(),
        parameterValues: parameterValues as Record<string, unknown>,
        cronExpression,
        monthlyRunCap,
        sortRotation: useRotation ? DEFAULT_SORT_ROTATION : null,
      });
      onClose();
    } catch (err) {
      setSubmitError((err as Error).message);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="space-y-1.5">
        <Label htmlFor="schedule-template">Template</Label>
        <select
          id="schedule-template"
          value={templateId}
          onChange={(e) => {
            setTemplateId(e.target.value);
            setParameterValues({});
          }}
          className="w-full h-10 px-3 bg-surface border border-border-default rounded-sm text-base"
        >
          <option value="">— Select a template —</option>
          {templates.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name} ({t.origin})
            </option>
          ))}
        </select>
      </div>

      {tpl && tpl.parameters.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs uppercase tracking-wider text-fg-tertiary">Template parameters</p>
          <TemplateParameterForm
            parameters={tpl.parameters as TemplateParameterSpec[]}
            values={parameterValues}
            onChange={setParameterValues}
          />
        </div>
      )}

      <div className="space-y-1.5">
        <Label htmlFor="schedule-name">Name</Label>
        <Input
          id="schedule-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Weekly Pet Niche Scan"
          maxLength={120}
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="schedule-cron-preset">Cron preset</Label>
        <select
          id="schedule-cron-preset"
          value={presetIdx}
          onChange={(e) => setPresetIdx(Number(e.target.value))}
          className="w-full h-10 px-3 bg-surface border border-border-default rounded-sm text-base"
        >
          {CRON_PRESETS.map((p, idx) => (
            <option key={p.label} value={idx}>
              {p.label} {p.expression ? `(${p.expression})` : '— specify your own expression below'}
            </option>
          ))}
        </select>
        {CRON_PRESETS[presetIdx]!.expression === '' && (
          <Input
            value={customCron}
            onChange={(e) => setCustomCron(e.target.value)}
            placeholder="*/15 * * * *"
            className="font-mono"
          />
        )}
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handlePreview}
            loading={previewCron.isPending}
          >
            Preview next fire
          </Button>
          {previewResult && previewResult.valid && (
            <span className="text-xs text-fg-secondary">
              Next fire:{' '}
              <strong className="text-fg-primary">
                {new Date(previewResult.nextFireAt).toLocaleString()}
              </strong>{' '}
              ({relativeTime(new Date(previewResult.nextFireAt))})
            </span>
          )}
          {previewResult && !previewResult.valid && (
            <span className="text-xs text-negative">Invalid cron: {previewResult.error}</span>
          )}
        </div>
        <p className="text-xs text-fg-tertiary">
          5-field cron in UTC (min hour dom mon dow). Custom example:{' '}
          <code className="font-mono">0 9 * * 1</code> = every Monday 9am UTC.
        </p>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="schedule-cap">Monthly run cap</Label>
        <Input
          id="schedule-cap"
          type="number"
          min={1}
          max={1000}
          value={monthlyRunCap}
          onChange={(e) => setMonthlyRunCap(Number(e.target.value) || 30)}
        />
        <p className="text-xs text-fg-tertiary">
          Maximum number of times this schedule can fire per 30-day window. Default 30.
        </p>
      </div>

      <div className="space-y-1.5 border-t border-border-subtle pt-4">
        <label className="flex items-start gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={useRotation}
            onChange={(e) => setUseRotation(e.target.checked)}
            className="mt-1"
          />
          <span>
            <strong className="text-fg-primary">Rotate sorts for more variety</strong>
            <p className="text-fg-secondary text-xs mt-0.5">
              Cycle through 5 different Keepa sort orders (top sellers, most-reviewed, high volume,
              niche, new entrants). Each fire surfaces a different slice of the catalog — roughly 5×
              the discoverable surface area vs. a single sort.
            </p>
          </span>
        </label>
      </div>

      <label className="flex items-start gap-2 text-sm cursor-pointer">
        <input
          type="checkbox"
          checked={acknowledged}
          onChange={(e) => setAcknowledged(e.target.checked)}
          className="mt-1"
        />
        <span className="text-fg-secondary">
          I authorize this schedule to run unattended. Sourcito will require my re-confirmation
          every 30 days.
        </span>
      </label>

      {submitError && <p className="text-sm text-negative">{submitError}</p>}

      <DialogFooter>
        <DialogClose asChild>
          <Button variant="ghost" type="button">
            Cancel
          </Button>
        </DialogClose>
        <Button type="submit" loading={create.isPending}>
          Create schedule
        </Button>
      </DialogFooter>
    </form>
  );
}

function daysUntil(iso: string): number {
  const ms = new Date(iso).getTime() - Date.now();
  return Math.floor(ms / (24 * 60 * 60 * 1000));
}

function relativeTime(date: Date, opts: { past?: string; future?: string } = {}): string {
  const diff = date.getTime() - Date.now();
  const abs = Math.abs(diff);
  const min = 60_000;
  const hour = 60 * min;
  const day = 24 * hour;
  const fmt = (n: number, unit: string) =>
    diff < 0 ? `${opts.past ?? ''}${n}${unit} ago` : `${opts.future ?? 'in '}${n}${unit}`;
  if (abs < min) return diff < 0 ? 'just now' : 'imminent';
  if (abs < hour) return fmt(Math.round(abs / min), 'm');
  if (abs < day) return fmt(Math.round(abs / hour), 'h');
  return fmt(Math.round(abs / day), 'd');
}
